import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, MapPin, Phone, Wrench, ChevronRight } from "lucide-react";
import Navbar from "../sections/Navbar";
import Footer from "../sections/Footer";
import { Button } from "../components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { SERVICE_PAGES, SERVICE_AREAS, SERVICE_PAGE_LIST } from "../lib/services-data";
import { useSeo, LOCAL_BUSINESS_JSONLD } from "../lib/seo";
import { BUSINESS } from "../lib/site";

export default function ServicePage() {
  const { slug } = useParams();
  const data = SERVICE_PAGES[slug];

  // SEO must run unconditionally — call hook before any returns.
  useSeo({
    title: data?.title || "Repair · Tombs Electronic Repair",
    description: data?.metaDescription,
    canonical: typeof window !== "undefined" ? window.location.href : undefined,
    jsonLd: data
      ? {
          "@context": "https://schema.org",
          "@graph": [
            LOCAL_BUSINESS_JSONLD,
            {
              "@context": "https://schema.org",
              "@type": "Service",
              serviceType: `${data.device} Repair`,
              provider: { "@id": LOCAL_BUSINESS_JSONLD["@id"] },
              areaServed: SERVICE_AREAS.map((a) => ({ "@type": "Place", name: a })),
              description: data.metaDescription,
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: data.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }
      : undefined,
  });

  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="bg-[#0A0A0A] text-white min-h-screen" data-testid={`service-page-${data.slug}`}>
      <Navbar />

      <main className="pt-24 sm:pt-32">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 sm:px-8 mb-12">
          <nav className="font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 flex items-center gap-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/" className="hover:text-white">Services</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{data.short}</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 sm:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-end">
            <div className="lg:col-span-2">
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-[#0055FF] mb-4">
                {data.short.toUpperCase()} REPAIR · SUSQUEHANNA, PA
              </div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
                {data.h1}
              </h1>
              <p className="text-lg text-zinc-400 leading-relaxed max-w-2xl mb-8">
                {data.intro}
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="/#booking">
                  <Button
                    size="lg"
                    className="bg-[#0055FF] hover:bg-blue-600 text-white rounded-md font-semibold px-8 py-6 text-base"
                    data-testid={`service-${data.slug}-cta`}
                  >
                    Book this repair <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
                <a href={BUSINESS.phoneHref}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="bg-transparent border-zinc-700 text-white hover:bg-white/5 hover:text-white rounded-md font-semibold px-8 py-6 text-base"
                  >
                    <Phone className="w-4 h-4 mr-2" /> {BUSINESS.phone}
                  </Button>
                </a>
              </div>
            </div>

            <div className="bg-[#121212] border border-zinc-800 rounded-md p-6">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-3">QUICK FACTS</div>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#0055FF] mt-0.5" />
                  <div>
                    <div className="text-zinc-500 text-xs">Drop-off / Mail-in</div>
                    <div className="text-white">{BUSINESS.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#0055FF] mt-0.5" />
                  <div>
                    <div className="text-zinc-500 text-xs">Direct line</div>
                    <a href={BUSINESS.phoneHref} className="text-white hover:underline">{BUSINESS.phone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Wrench className="w-4 h-4 text-[#0055FF] mt-0.5" />
                  <div>
                    <div className="text-zinc-500 text-xs">Diagnostic</div>
                    <div className="text-white">Free before any work</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Common issues / pricing */}
        <section className="border-t border-white/5 py-20">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="mb-12">
              <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">
                COMMON {data.short.toUpperCase()} ISSUES I FIX
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter">
                Honest pricing · no surprises
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.commonIssues.map((issue, idx) => (
                <div
                  key={idx}
                  className="tech-card bg-[#121212] border border-zinc-800 rounded-md p-6"
                  data-testid={`service-${data.slug}-issue-${idx}`}
                >
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="font-display font-semibold text-lg tracking-tight">{issue.title}</h3>
                    <span className="font-mono text-sm text-[#FFCC00] whitespace-nowrap ml-3">{issue.price}</span>
                  </div>
                  <p className="text-zinc-400 text-sm leading-relaxed">{issue.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service area */}
        <section className="border-t border-white/5 py-20 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">SERVICE AREA</div>
                <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter mb-4">
                  Local to <span className="text-[#0055FF]">Susquehanna County</span>, serving NEPA &amp; the Southern Tier.
                </h2>
                <p className="text-zinc-400 leading-relaxed mb-6">
                  {slug === "pc"
                    ? "PCs are in-person only — drop-off and pickup at 67 Grand St, Susquehanna PA. No shipping for desktop or gaming PC repairs."
                    : "Drop-off and pickup in person, or mail it in from anywhere — return shipping available."}{" "}
                  Most local customers come from these towns:
                </p>
              </div>
              <ul className="grid grid-cols-2 gap-3 self-center">
                {SERVICE_AREAS.map((area) => (
                  <li key={area} className="flex items-center gap-2 text-zinc-300">
                    <Check className="w-4 h-4 text-[#34C759] flex-shrink-0" />
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="border-t border-white/5 py-20">
          <div className="max-w-4xl mx-auto px-6 sm:px-8">
            <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">FAQ</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter mb-10">
              Common questions about {data.short} repair
            </h2>

            <Accordion type="single" collapsible className="space-y-3">
              {data.faqs.map((f, idx) => (
                <AccordionItem
                  key={idx}
                  value={`faq-${idx}`}
                  className="bg-[#121212] border border-zinc-800 rounded-md px-6"
                  data-testid={`service-${data.slug}-faq-${idx}`}
                >
                  <AccordionTrigger className="font-display font-semibold text-left text-lg hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-400 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Other services */}
        <section className="border-t border-white/5 py-20 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">OTHER REPAIRS</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter mb-10">
              Looking for something else?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {SERVICE_PAGE_LIST.filter((p) => p.slug !== data.slug).map((p) => (
                <Link
                  key={p.slug}
                  to={`/repair/${p.slug}`}
                  className="bg-[#121212] border border-zinc-800 rounded-md p-5 hover:border-[#0055FF] hover:bg-[#1a1a1a] transition-colors"
                  data-testid={`service-${data.slug}-link-${p.slug}`}
                >
                  <div className="font-display font-semibold mb-1">{p.short}</div>
                  <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500">Repair →</div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="border-t border-white/5 py-20">
          <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
            <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tighter mb-6">
              Ready to fix your {data.short}?
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-8 max-w-xl mx-auto">
              Submit a quick request — I'll respond personally with next steps and a quote.
            </p>
            <a href="/#booking">
              <Button
                size="lg"
                className="bg-[#0055FF] hover:bg-blue-600 text-white rounded-md font-semibold px-8 py-6 text-base"
                data-testid={`service-${data.slug}-final-cta`}
              >
                Book a {data.short} repair <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
